import React, { useContext } from "react";
import { menu } from "./menu";
import { useNavigate, useLocation } from "react-router-dom";
import "./sidebar.css";
import Lang from "../content/components/Lang";
import { UserContext } from "../../Context";
import { Hexagon } from "lucide-react";

/**
 * Premium Sidebar Navigation Component
 *
 * Renders a fixed sidebar with grouped navigation items. Each item displays
 * a lucide-react icon and a translated label. The active route is highlighted
 * with a brand-colored accent bar using dynamic Tailwind classes.
 *
 * @author Forge + opencode (glm-5.1)
 * @version 3.0
 * @since 2026-04-17
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  

  const handleMenu = (href) => {
    navigate(href);
  };

  /**
   * Checks if the given path is currently active.
   * Handles both exact matches and sub-paths for better UX.
   *
   * @param {string} href - The route path to check
   * @returns {boolean} Whether the route is active
   */
  const isPathActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <aside className="side-bar-container">
      {/* Brand Header */}
      <div className="sidebar-brand-header">
        <div
          className="sidebar-brand-icon"
        >
          <Hexagon size={18} strokeWidth={2.5} />
        </div>
        <div className="sidebar-brand-text">
          <span className="sidebar-brand-name">HT Explore</span>
          <span className="sidebar-brand-subtitle">Management</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav-scroll" aria-label="Main Navigation">
        {menu
          .filter(
            (group) =>
              group.access === undefined ||
              (user?.roles && user.roles.includes(group.access))
          )
          .map((group) => (
            <section key={group.title} className="sidebar-group">
              <h3 className="sidebar-group-title">{group.title}</h3>
              <div className="sidebar-items-container">
                {group.items.map((item, index) => {
                  const active = isPathActive(item.href);
                  const IconComponent = item.icon;

                  return (
                    <div
                      key={`${item.href}-${index}`}
                      onClick={() => handleMenu(item.href)}
                      className={`sidebar-item ${active ? "active" : ""}`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleMenu(item.href)
                      }
                      aria-current={active ? "page" : undefined}
                    >
                      <span className="sidebar-icon" aria-hidden="true">
                        {React.isValidElement(IconComponent) ? (
                          IconComponent
                        ) : (
                          <IconComponent size={18} />
                        )}
                      </span>
                      <span className="sidebar-label">
                        <Lang w={item.name} />
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
      </nav>
    </aside>
  );
}
